import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import './utils/financeCalculations';
import './utils/portfolioCalculations';
import { demoRepository } from './repository/demoRepository';
import pocketbaseService, { REQUIRED_COLLECTIONS } from './services/pocketbaseService';
import App from './App.jsx';
import './index.css';

const bootAuthState = pocketbaseService.auth.getState();
if (pocketbaseService.isEnabled && bootAuthState.token && !bootAuthState.isAuthenticated) {
  pocketbaseService.auth.logout();
}

/*
 * Initial workspace loading previously requested every collection sequentially.
 * Keep the exact same collection set and cache contract, but load in small bounded
 * batches so network latency is not multiplied across the full collection list.
 * If the optimized path itself fails unexpectedly, fall back to the original loader.
 */
if (!pocketbaseService.__boundedParallelWorkspaceLoadApplied) {
  const originalLoadAllCollections = pocketbaseService.loadAllCollections.bind(pocketbaseService);
  const BATCH_SIZE = 6;
  const CORE_COLLECTIONS = ['properties', 'rentable_spaces', 'tenants', 'leases', 'transactions'];

  pocketbaseService.loadAllCollections = async () => {
    try {
      const health = await pocketbaseService.getHealth();
      if (!health?.success) {
        return {
          success: false,
          message: health?.message || 'Workspace data is currently unavailable.',
          data: {},
          recordCounts: {},
          totalRecords: 0,
          errors: {}
        };
      }

      const loadedData = {};
      const errors = {};

      for (let index = 0; index < REQUIRED_COLLECTIONS.length; index += BATCH_SIZE) {
        const batch = REQUIRED_COLLECTIONS.slice(index, index + BATCH_SIZE);
        const batchResults = await Promise.all(batch.map(async (collectionName) => {
          try {
            const records = await pocketbaseService.loadCollection(collectionName);
            return {
              collectionName,
              records: Array.isArray(records) ? records : [],
              error: null
            };
          } catch (error) {
            return {
              collectionName,
              records: [],
              error: error?.message || 'Collection load failed.'
            };
          }
        }));

        batchResults.forEach(({ collectionName, records, error }) => {
          loadedData[collectionName] = records;
          if (error) errors[collectionName] = error;
        });
      }

      const coreLoaded = CORE_COLLECTIONS.every(collectionName => Array.isArray(loadedData[collectionName]));
      if (!coreLoaded) return originalLoadAllCollections();

      const recordCounts = Object.fromEntries(
        Object.entries(loadedData).map(([key, value]) => [key, value.length])
      );
      const totalRecords = Object.values(recordCounts).reduce((sum, count) => sum + count, 0);
      const finalMode = totalRecords > 0 ? 'backend_ready' : 'backend_empty';
      pocketbaseService.setActiveDataSource(finalMode);

      return {
        success: true,
        data: loadedData,
        recordCounts,
        totalRecords,
        errors: Object.keys(errors).length > 0 ? errors : undefined
      };
    } catch (error) {
      console.warn('[MPP] Optimized workspace load fell back to standard loading:', error?.message);
      return originalLoadAllCollections();
    }
  };

  pocketbaseService.__boundedParallelWorkspaceLoadApplied = true;
}

const WORKSPACE_SYNC_EVENT = 'mpp:workspace-sync-state';
const emitWorkspaceSyncState = (detail) => {
  window.__mppWorkspaceSyncSnapshot = detail;
  window.dispatchEvent(new CustomEvent(WORKSPACE_SYNC_EVENT, { detail }));
};

/*
 * Gate only the first authenticated workspace refresh of the session. This also
 * deduplicates the login-triggered refresh and the authenticated startup refresh,
 * while leaving later manual refresh behaviour unchanged.
 */
if (!demoRepository.__initialWorkspaceSyncGateApplied) {
  const originalRefreshFromPocketBase = demoRepository.refreshFromPocketBase.bind(demoRepository);
  let initialWorkspaceRefreshPromise = null;

  demoRepository.refreshFromPocketBase = async (options = {}) => {
    const authState = pocketbaseService.auth.getState();
    const shouldGateInitialLoad = Boolean(
      options?.force
      && authState?.isAuthenticated
      && !demoRepository.isManualDemoMode?.()
      && !window.__mppInitialWorkspaceSyncComplete
    );

    if (!shouldGateInitialLoad) return originalRefreshFromPocketBase(options);
    if (initialWorkspaceRefreshPromise) return initialWorkspaceRefreshPromise;

    emitWorkspaceSyncState({ status: 'syncing', startedAt: Date.now() });

    initialWorkspaceRefreshPromise = (async () => {
      try {
        const result = await originalRefreshFromPocketBase(options);
        if (result?.success) {
          window.__mppInitialWorkspaceSyncComplete = true;
          emitWorkspaceSyncState({
            status: 'complete',
            startedAt: window.__mppWorkspaceSyncSnapshot?.startedAt || Date.now(),
            completedAt: Date.now(),
            totalRecords: result.totalRecords || 0
          });
        } else {
          emitWorkspaceSyncState({
            status: 'error',
            message: 'We could not finish preparing your workspace. No changes can be made until loading completes.'
          });
        }
        return result;
      } catch (error) {
        emitWorkspaceSyncState({
          status: 'error',
          message: 'We could not finish preparing your workspace. No changes can be made until loading completes.'
        });
        throw error;
      }
    })();

    try {
      return await initialWorkspaceRefreshPromise;
    } finally {
      initialWorkspaceRefreshPromise = null;
    }
  };

  demoRepository.__initialWorkspaceSyncGateApplied = true;
}

const WORKSPACE_STAGES = [
  'Securing your workspace',
  'Loading portfolio records',
  'Preparing properties and tenancy data',
  'Preparing finance and document records',
  'Refreshing portfolio calculations',
  'Finalising your workspace'
];

const WorkspaceSyncShell = ({ children }) => {
  const [syncState, setSyncState] = useState(() => window.__mppWorkspaceSyncSnapshot || { status: 'idle' });
  const [stageIndex, setStageIndex] = useState(0);

  useEffect(() => {
    const handleSyncState = (event) => {
      const nextState = event?.detail || { status: 'idle' };
      setSyncState(nextState);
      if (nextState.status === 'syncing') setStageIndex(0);
    };

    window.addEventListener(WORKSPACE_SYNC_EVENT, handleSyncState);
    if (window.__mppWorkspaceSyncSnapshot) setSyncState(window.__mppWorkspaceSyncSnapshot);
    return () => window.removeEventListener(WORKSPACE_SYNC_EVENT, handleSyncState);
  }, []);

  useEffect(() => {
    if (syncState.status !== 'syncing') return undefined;
    const intervalId = window.setInterval(() => {
      setStageIndex(current => Math.min(current + 1, WORKSPACE_STAGES.length - 1));
    }, 1200);
    return () => window.clearInterval(intervalId);
  }, [syncState.status]);

  useEffect(() => {
    if (syncState.status !== 'complete') return undefined;
    const timeoutId = window.setTimeout(() => {
      setSyncState({ status: 'idle' });
      window.__mppWorkspaceSyncSnapshot = { status: 'idle' };
    }, 850);
    return () => window.clearTimeout(timeoutId);
  }, [syncState.status]);

  const retryWorkspaceLoad = async () => {
    try {
      const result = await demoRepository.refreshFromPocketBase({ force: true, silent: true, notify: true });
      if (result?.success && typeof demoRepository.startBackendAutoSync === 'function') {
        demoRepository.startBackendAutoSync();
      }
    } catch (error) {
      emitWorkspaceSyncState({
        status: 'error',
        message: 'We could not finish preparing your workspace. Please try again.'
      });
    }
  };

  const logoutFromGate = () => {
    pocketbaseService.auth.logout();
    demoRepository.stopBackendAutoSync?.();
    window.location.reload();
  };

  const gateVisible = syncState.status === 'syncing' || syncState.status === 'complete' || syncState.status === 'error';

  return (
    <>
      {children}
      {gateVisible && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/80 px-4 py-8 backdrop-blur-md">
          <div className="w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-white shadow-2xl">
            <div className="border-b border-slate-100 bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 px-6 py-6 text-white">
              <div className="flex items-center gap-3">
                <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${syncState.status === 'error' ? 'bg-red-500/20 text-red-200' : syncState.status === 'complete' ? 'bg-emerald-500/20 text-emerald-200' : 'bg-amber-400/20 text-amber-200'}`}>
                  {syncState.status === 'error' ? '!' : syncState.status === 'complete' ? '✓' : <span className="h-5 w-5 animate-spin rounded-full border-2 border-amber-200 border-t-transparent" />}
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-300">Malhotra's Property Portfolio</div>
                  <h2 className="mt-1 text-xl font-semibold">
                    {syncState.status === 'error' ? 'Workspace not ready' : syncState.status === 'complete' ? 'Workspace ready' : 'Preparing your workspace'}
                  </h2>
                </div>
              </div>
              <p className="mt-4 text-sm leading-6 text-slate-300">
                {syncState.status === 'error'
                  ? (syncState.message || 'We could not finish preparing your workspace.')
                  : syncState.status === 'complete'
                    ? 'Your latest portfolio data is ready to use.'
                    : 'Please wait until loading is complete before opening records or making changes.'}
              </p>
            </div>

            <div className="p-6">
              {syncState.status === 'error' ? (
                <div>
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm leading-5 text-red-800">
                    Your workspace remains locked to prevent accidental changes while data is incomplete.
                  </div>
                  <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <button type="button" onClick={logoutFromGate} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                      Log Out
                    </button>
                    <button type="button" onClick={retryWorkspaceLoad} className="rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">
                      Retry Loading
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-5 h-2 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${syncState.status === 'complete' ? 'bg-emerald-500' : 'bg-amber-500'}`}
                      style={{ width: syncState.status === 'complete' ? '100%' : `${Math.max(12, ((stageIndex + 1) / WORKSPACE_STAGES.length) * 100)}%` }}
                    />
                  </div>

                  <div className="space-y-3">
                    {WORKSPACE_STAGES.map((stage, index) => {
                      const complete = syncState.status === 'complete' || index < stageIndex;
                      const active = syncState.status === 'syncing' && index === stageIndex;
                      return (
                        <div key={stage} className={`flex items-center gap-3 text-sm ${complete ? 'text-emerald-700' : active ? 'font-medium text-amber-700' : 'text-slate-400'}`}>
                          <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border text-[10px] ${complete ? 'border-emerald-200 bg-emerald-50' : active ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-white'}`}>
                            {complete ? '✓' : active ? '•' : ''}
                          </span>
                          <span>{stage}</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const normaliseStatus = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '_');
const clamp = (value, min, max) => Math.min(max, Math.max(min, Number(value) || 0));

const isOwnerOccupiedProperty = (property) => {
  const financial = property?.metadata?.financial || {};
  const loanType = normaliseStatus(property?.loanType || financial.loanType || '');
  const explicitUse = normaliseStatus(property?.propertyUse || property?.useType || property?.occupancyType || '');
  return loanType.includes('owner_occupier')
    || explicitUse.includes('owner_occupied')
    || explicitUse.includes('primary_residence')
    || explicitUse === 'ppr';
};

if (!demoRepository.__ownerOccupiedOrderingApplied) {
  const originalGetAllProperties = demoRepository.getAllProperties.bind(demoRepository);
  demoRepository.getAllProperties = (...args) => {
    const properties = originalGetAllProperties(...args) || [];
    if (window.location.pathname !== '/') return properties;
    return properties
      .map((property, index) => ({ property, index }))
      .sort((a, b) => {
        const aOwnerOccupied = isOwnerOccupiedProperty(a.property);
        const bOwnerOccupied = isOwnerOccupiedProperty(b.property);
        if (aOwnerOccupied !== bOwnerOccupied) return aOwnerOccupied ? -1 : 1;
        if (aOwnerOccupied && bOwnerOccupied) {
          return String(a.property?.name || '').localeCompare(String(b.property?.name || ''), 'en-AU', { sensitivity: 'base' });
        }
        return a.index - b.index;
      })
      .map(({ property }) => property);
  };
  demoRepository.__ownerOccupiedOrderingApplied = true;
}

const getPropertyImageFileUrl = (record, filename) => {
  if (!record?.id || !filename) return '';

  const mappedUrl = pocketbaseService.mappers.getPocketBaseFileUrl('properties', record, filename);
  if (mappedUrl) return mappedUrl;

  const baseUrl = String(import.meta.env.VITE_POCKETBASE_URL || '').trim().replace(/\/$/, '');
  if (!baseUrl) return '';
  const collectionRef = record.collectionId || record.collectionName || 'properties';
  return `${baseUrl}/api/files/${encodeURIComponent(collectionRef)}/${encodeURIComponent(record.id)}/${encodeURIComponent(filename)}`;
};

if (!pocketbaseService.mappers.__propertyImageSupportApplied) {
  const originalMapPocketBaseToApp = pocketbaseService.mappers.mapPocketBaseToApp;
  const mapPocketBaseToAppWithPropertyImages = (collectionName, record) => {
    const mapped = originalMapPocketBaseToApp(collectionName, record);
    if (collectionName !== 'properties' || !mapped || !record) return mapped;

    const imageNames = Array.isArray(record.propertyImages)
      ? record.propertyImages.filter(Boolean)
      : record.propertyImages
        ? [record.propertyImages]
        : [];
    const imageUrls = imageNames
      .map(name => getPropertyImageFileUrl(record, name))
      .filter(Boolean);
    const thumbUrls = imageUrls.map(url => `${url}${url.includes('?') ? '&' : '?'}thumb=300x300`);

    mapped.propertyImages = imageNames;
    mapped.propertyImageUrls = imageUrls;
    mapped.propertyImageThumbUrls = thumbUrls;
    mapped.imageUrl = imageUrls[0] || thumbUrls[0] || '';
    mapped.thumbnailUrl = thumbUrls[0] || imageUrls[0] || '';
    return mapped;
  };

  pocketbaseService.mappers.mapPocketBaseToApp = mapPocketBaseToAppWithPropertyImages;
  pocketbaseService.mappers.mapPocketBaseRecordToApp = mapPocketBaseToAppWithPropertyImages;
  pocketbaseService.mappers.__propertyImageSupportApplied = true;
}

const getCardImageDisplay = (property) => {
  const metadata = property?.metadata && typeof property.metadata === 'object' && !Array.isArray(property.metadata)
    ? property.metadata
    : {};
  const saved = metadata.cardImage || {};
  return {
    x: clamp(saved.x ?? 50, 0, 100),
    y: clamp(saved.y ?? 45, 0, 100),
    zoom: clamp(saved.zoom ?? 1, 1, 1.6)
  };
};

const applyPropertyCardImageFraming = (root = document) => {
  if (!root?.querySelectorAll) return;
  const properties = demoRepository.getAllProperties?.() || [];
  const byName = new Map(properties.map(property => [String(property?.name || '').trim(), property]));

  root.querySelectorAll('article[role="link"][aria-label^="Open "][aria-label$=" details"]').forEach(article => {
    const match = String(article.getAttribute('aria-label') || '').match(/^Open (.+) details$/);
    if (!match) return;
    const property = byName.get(match[1].trim());
    if (!property) return;
    const image = article.querySelector(':scope > div:nth-child(2) > div:first-child img');
    if (!image) return;
    const display = getCardImageDisplay(property);
    image.style.setProperty('--mpp-card-image-x', `${display.x}%`);
    image.style.setProperty('--mpp-card-image-y', `${display.y}%`);
    image.style.setProperty('--mpp-card-image-zoom', String(display.zoom));
  });
};

const replaceLongDashesInNode = (node) => {
  if (!node) return;
  if (node.nodeType === Node.TEXT_NODE) {
    if (/[—–]/.test(node.nodeValue || '')) node.nodeValue = String(node.nodeValue || '').replace(/[—–]/g, '-');
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);
  textNodes.forEach(textNode => {
    if (/[—–]/.test(textNode.nodeValue || '')) textNode.nodeValue = String(textNode.nodeValue || '').replace(/[—–]/g, '-');
  });
};

const rootElement = document.getElementById('root');
if (rootElement && !window.__mppLongDashNormalizer) {
  let framingFrame = null;
  const schedulePropertyCardFraming = () => {
    if (framingFrame) cancelAnimationFrame(framingFrame);
    framingFrame = requestAnimationFrame(() => {
      framingFrame = null;
      applyPropertyCardImageFraming(rootElement);
    });
  };

  const observer = new MutationObserver(mutations => {
    mutations.forEach(mutation => {
      if (mutation.type === 'characterData') replaceLongDashesInNode(mutation.target);
      mutation.addedNodes?.forEach(replaceLongDashesInNode);
    });
    schedulePropertyCardFraming();
  });
  observer.observe(rootElement, { childList: true, subtree: true, characterData: true });
  window.addEventListener('mpp:data-refreshed', schedulePropertyCardFraming);
  window.__mppLongDashNormalizer = observer;
  window.__mppPropertyCardFramingRefresh = schedulePropertyCardFraming;
}

createRoot(rootElement).render(
  <React.StrictMode>
    <WorkspaceSyncShell>
      <App />
    </WorkspaceSyncShell>
  </React.StrictMode>
);